import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Search } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { getGoogleClientId } from "@/api/auth";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: Record<string, unknown>) => void;
          renderButton: (el: HTMLElement, config: Record<string, unknown>) => void;
        };
      };
    };
  }
}

export default function Login() {
  const navigate = useNavigate();
  const { user, loading: authLoading, loginGoogle } = useAuth();
  const [googleReady, setGoogleReady] = useState(false);

  useEffect(() => {
    // Only redirect after auth loading is complete to avoid redirect loop with stale cached user
    if (!authLoading && user) navigate("/", { replace: true });
  }, [user, authLoading, navigate]);

  const handleGoogleCallback = useCallback(async (response: { credential: string }) => {
    const { error } = await loginGoogle(response.credential);
    if (error) {
      toast.error(error);
    } else {
      toast.success("登入成功");
      navigate("/");
    }
  }, [loginGoogle, navigate]);

  useEffect(() => {
    let mounted = true;

    async function initGoogle() {
      const clientId = await getGoogleClientId();
      if (!clientId || !mounted) return;

      // Wait for Google script to load
      const waitForGoogle = () => new Promise<void>((resolve) => {
        if (window.google) { resolve(); return; }
        const check = setInterval(() => {
          if (window.google) { clearInterval(check); resolve(); }
        }, 100);
        setTimeout(() => { clearInterval(check); resolve(); }, 5000);
      });

      await waitForGoogle();
      if (!window.google || !mounted) return;

      try {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleCallback,
        });

        const btnEl = document.getElementById("google-signin-btn");
        if (btnEl) {
          window.google.accounts.id.renderButton(btnEl, {
            theme: "outline",
            size: "large",
            width: 360,
            text: "signin_with",
          });
        }

        if (mounted) setGoogleReady(true);
      } catch (err) {
        console.error("Google Sign-In init failed:", err);
      }
    }

    if (!authLoading && !user) {
      initGoogle();
    }

    return () => { mounted = false; };
  }, [authLoading, user, handleGoogleCallback]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary text-primary-foreground flex-col justify-center items-center p-12">
        <div className="max-w-md space-y-6">
          <div className="flex items-center gap-3">
            <Search className="h-10 w-10" />
            <h1 className="text-4xl font-bold">SEO 分析器</h1>
          </div>
          <p className="text-lg text-primary-foreground/80">
            專業的搜尋引擎結果頁面分析工具，幫助您深入了解關鍵字排名與競爭對手表現。
          </p>
          <div className="space-y-2 text-sm text-primary-foreground/70">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-primary-foreground/50" />
              <span>即時 SERP 分析</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-primary-foreground/50" />
              <span>自動化網頁爬蟲</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-primary-foreground/50" />
              <span>SEO 評分與建議</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right login panel */}
      <div className="flex-1 flex items-center justify-center bg-background p-4 lg:p-8">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">歡迎回來</CardTitle>
            <CardDescription className="text-center">
              使用 Google 帳號登入以繼續使用
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4">
            <div id="google-signin-btn" className="min-h-[44px]">
              {!googleReady && (
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  載入 Google 登入...
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
